import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Info, Sparkles, Wand2, X } from 'lucide-react';

import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import {
  useGenerate3dFromImagesMutation,
  useGenerate3dFromTextMutation,
  useGenerateAvatarMutation,
  useGetModelsQuery,
  useGetTemplateDetailQuery,
  useGetTemplatesQuery,
  type AvatarItem,
  type AvatarModelInfo,
  type AvatarTemplate,
} from '@/services/avatarApi';
import { Direct3dModal } from '@/components/avatars/Direct3dModal';
import { GeneratingOverlay } from '@/components/avatars/GeneratingOverlay';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { isCreationReserved } from '@/lib/serverErrorCode';
import { PromptInIngleseHint } from '@/components/avatars/PromptInIngleseHint';
import { AvvisoSfondoTolto } from '@/components/avatars/AvvisoSfondoTolto';

const PROMPT_MAX = 500;
const FALLBACK_DEFAULT_MODEL = 'gemini-2.5-flash-image';
const MAX_SOURCE_IMAGE_BYTES = 15 * 1024 * 1024; // matches backend multer cap
/** Questa schermata non fa scegliere lo sfondo: genera SEMPRE col fondo trasparente (e per questo mostra l'avviso vicino alla descrizione). */
const SFONDO_TRASPARENTE = true;

function pickDefaultModelId(models: AvatarModelInfo[]): string {
  const flagged = models.find((m) => m.isDefault && !m.deprecated);
  if (flagged) return flagged.id;
  const nonDeprecated = models.find((m) => !m.deprecated);
  return nonDeprecated?.id ?? FALLBACK_DEFAULT_MODEL;
}

export function AvatarCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('avatarAssistant');
  const { t: ta } = useTranslation('avatars');
  const haptics = useHaptics();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Lazy initializer: seed from the assistant wizard's handoff (router
  // location.state) exactly once on mount. Runs once, so it never clobbers
  // user edits on re-render; null on a cold load/refresh → empty textarea.
  const [prompt, setPrompt] = useState(() => {
    const s = location.state as { assistantPrompt?: string } | null;
    return s?.assistantPrompt?.slice(0, PROMPT_MAX) ?? '';
  });
  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const [inspectTemplateId, setInspectTemplateId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Photo-base input — when set, the request switches to multipart/form-data
  // and the prompt becomes optional. Preview is a transient object URL.
  const [sourceImage, setSourceImage] = useState<File | null>(null);
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState<string | null>(null);

  const { data: templates = [] } = useGetTemplatesQuery();
  const { data: models = [] } = useGetModelsQuery();
  const [generateAvatar, { isLoading: submitting }] = useGenerateAvatarMutation();

  // Phase 5+ direct 3D paths (bypass the 2D step).
  const [direct3dOpen, setDirect3dOpen] = useState(false);
  const [generate3dFromText, { isLoading: text3dPending }] = useGenerate3dFromTextMutation();
  const [generate3dFromImages, { isLoading: multi3dPending }] = useGenerate3dFromImagesMutation();
  const direct3dPending = text3dPending || multi3dPending;

  const onGenerateDirect3dText = useCallback(async (textPrompt: string, title?: string) => {
    try {
      const res = await generate3dFromText({ textPrompt, title, templateId }).unwrap();
      setDirect3dOpen(false);
      navigate(`/avatars/${res.avatarId}`);
    } catch (err) {
      console.error('[AvatarCreate] direct text-to-3d failed', err);
    }
  }, [generate3dFromText, navigate, templateId]);

  const onGenerateDirect3dMultiImage = useCallback(async (files: File[], title?: string) => {
    try {
      const res = await generate3dFromImages({ images: files, title, templateId }).unwrap();
      setDirect3dOpen(false);
      navigate(`/avatars/${res.avatarId}`);
    } catch (err) {
      console.error('[AvatarCreate] direct multi-image-to-3d failed', err);
    }
  }, [generate3dFromImages, navigate, templateId]);

  const defaultModelId = useMemo(() => pickDefaultModelId(models), [models]);
  const effectiveModelId = modelId ?? defaultModelId;
  const activeModel = models.find((m) => m.id === effectiveModelId);
  const activeTemplate = templates.find((tpl) => tpl._id === templateId);

  // Source preview lifetime tracking — revoke the previous blob URL before
  // creating a new one (and on unmount) so we don't leak memory inside
  // long-lived Mini App sessions.
  useEffect(() => {
    if (!sourceImage) {
      if (sourcePreviewUrl) URL.revokeObjectURL(sourcePreviewUrl);
      setSourcePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(sourceImage);
    setSourcePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
    // sourcePreviewUrl is managed by this effect; including it in deps would loop.
  }, [sourceImage]);

  // With a photo, prompt is optional. Without a photo, prompt is required.
  const hasPrompt = prompt.trim().length > 0;
  const canSubmit = (hasPrompt || !!sourceImage) && !submitting && !activeJobId;

  const onPickPhoto = useCallback(() => {
    if (submitting || activeJobId) return;
    fileInputRef.current?.click();
  }, [submitting, activeJobId]);

  const onPhotoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so the same file can be re-selected after a clear.
      e.target.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setError(ta('create.pickImageFile'));
        return;
      }
      if (file.size > MAX_SOURCE_IMAGE_BYTES) {
        setError(ta('create.imageTooLarge', { mb: Math.round(MAX_SOURCE_IMAGE_BYTES / 1024 / 1024) }));
        return;
      }
      setError(null);
      haptics.impact('light');
      setSourceImage(file);
    },
    [haptics, ta],
  );

  const onClearPhoto = useCallback(() => {
    haptics.impact('light');
    setSourceImage(null);
  }, [haptics]);

  const onSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setError(null);
    haptics.impact('medium');
    try {
      const { jobId } = await generateAvatar({
        // Backend accepts empty prompt only when an image is provided. Send a
        // trimmed prompt; backend validation handles the edge cases.
        prompt: prompt.trim(),
        templateId,
        model: effectiveModelId,
        transparent: SFONDO_TRASPARENTE,
        sourceImage: sourceImage ?? undefined,
      }).unwrap();
      setActiveJobId(jobId);
    } catch (err) {
      console.error('[AvatarCreate] generate failed', err);
      setError(isCreationReserved(err) ? ta('common.creationReserved') : ta('create.startFailed'));
    }
  }, [canSubmit, generateAvatar, prompt, templateId, effectiveModelId, haptics, sourceImage, ta]);

  const onComplete = useCallback(
    (avatar: AvatarItem) => {
      haptics.notification('success');
      setActiveJobId(null);
      navigate(`/avatars/${avatar._id}`, { replace: true });
    },
    [navigate, haptics],
  );

  const onFailed = useCallback(
    (msg: string) => {
      haptics.notification('error');
      setActiveJobId(null);
      setError(msg);
    },
    [haptics],
  );

  useMainButton({
    text: submitting
      ? ta('create.sending')
      : sourceImage
        ? ta('create.generateFromPhoto')
        : ta('create.generate'),
    enabled: canSubmit,
    loading: submitting,
    onClick: onSubmit,
  });

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">{ta('create.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {ta('create.subtitle')}
        </p>
      </header>

      <Direct3dCard onOpen={() => setDirect3dOpen(true)} />

      <Direct3dModal
        open={direct3dOpen}
        isPending={direct3dPending}
        onClose={() => setDirect3dOpen(false)}
        onGenerateText={onGenerateDirect3dText}
        onGenerateMultiImage={onGenerateDirect3dMultiImage}
      />

      <div className="flex flex-col gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-fg-muted">
          {ta('create.photoAsBase')} <span className="font-normal lowercase text-fg-muted/70">{ta('common.optional')}</span>
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPhotoChange}
        />
        {sourceImage && sourcePreviewUrl ? (
          <div className={cn(
            'flex items-center gap-3 rounded-xl border border-border bg-bg-secondary p-2',
          )}>
            <img
              src={sourcePreviewUrl}
              alt={ta('create.photoPreviewAlt')}
              className="h-16 w-16 shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-fg">{sourceImage.name}</p>
              <p className="text-[11px] text-fg-muted">
                {(sourceImage.size / 1024).toFixed(0)} KB · {sourceImage.type || ta('create.imageFallback')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClearPhoto}
              aria-label={ta('create.removePhoto')}
              className="shrink-0 rounded-full border border-border bg-bg p-2 text-fg-muted active:opacity-80"
            >
              <X aria-hidden className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onPickPhoto}
            disabled={submitting || !!activeJobId}
            className={cn(
              'flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-bg-secondary px-4 py-4 text-sm font-medium text-fg',
              'transition-opacity active:opacity-80',
              (submitting || activeJobId) && 'opacity-60',
            )}
          >
            <ImagePlus aria-hidden className="h-4 w-4" />
            {ta('create.choosePhoto')}
          </button>
        )}
        <p className="text-[11px] text-fg-muted">
          {ta('create.photoHint')}
        </p>
      </div>

      <button
        type="button"
        onClick={() => navigate('/avatars/new/assistant')}
        className="flex items-center justify-between gap-3 rounded-2xl border border-brand bg-brand/10 px-4 py-3 text-left transition-opacity active:opacity-80"
      >
        <div className="flex items-center gap-3">
          <span className="rounded-xl bg-brand/20 p-2 text-brand">
            <Wand2 aria-hidden className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-bold text-fg">{t('entryCard.title')}</p>
            <p className="mt-0.5 text-[11px] text-fg-muted">{t('entryCard.subtitle')}</p>
          </div>
        </div>
        <span aria-hidden className="text-xl font-bold text-brand">›</span>
      </button>

      <div className="flex flex-col gap-2">
        <label htmlFor="avatar-prompt" className="text-xs font-bold uppercase tracking-wide text-fg-muted">
          {ta('create.describe')}{' '}
          {sourceImage && <span className="font-normal lowercase text-fg-muted/70">{ta('common.optional')}</span>}
        </label>
        <textarea
          id="avatar-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value.slice(0, PROMPT_MAX))}
          placeholder={
            sourceImage
              ? ta('create.placeholderWithPhoto')
              : ta('create.placeholder')
          }
          rows={5}
          className={cn(
            'w-full resize-y rounded-xl bg-bg-secondary p-3 text-sm text-fg placeholder:text-fg-muted',
            'border border-border focus:border-brand focus:outline-none',
          )}
        />
        <PromptInIngleseHint />
        {SFONDO_TRASPARENTE && <AvvisoSfondoTolto />}
        {activeTemplate?.guidelines?.trim() && (
          <div className="flex items-start gap-2 rounded-lg bg-bg-secondary/60 px-3 py-2 text-[11px] leading-relaxed text-fg-muted">
            <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-semibold">{ta('create.guidelines')}</span> {activeTemplate.guidelines.trim()}
            </span>
          </div>
        )}
        <p className="self-end text-[11px] text-fg-muted">
          {prompt.length}/{PROMPT_MAX}
        </p>
      </div>

      {templates.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-bold uppercase tracking-wide text-fg-muted">{ta('create.style')}</p>
          <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {templates.map((tpl) => {
              const active = templateId === tpl._id;
              return (
                <div
                  key={tpl._id}
                  className={cn(
                    'flex shrink-0 items-stretch overflow-hidden rounded-full border transition-colors',
                    active ? 'border-brand bg-brand text-brand-fg' : 'border-border bg-bg-secondary text-fg',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setTemplateId(active ? undefined : tpl._id)}
                    className="px-3 py-1.5 text-sm font-medium"
                  >
                    {tpl.icon ? `${tpl.icon} ` : ''}
                    {tpl.name}
                  </button>
                  <button
                    type="button"
                    aria-label={ta('create.inspect', { name: tpl.name })}
                    onClick={() => {
                      haptics.impact('light');
                      setInspectTemplateId(tpl._id);
                    }}
                    className={cn(
                      'flex w-8 items-center justify-center border-l text-xs font-bold',
                      active ? 'border-brand-fg/30 text-brand-fg/80' : 'border-border text-fg-muted',
                    )}
                  >
                    i
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {models.length > 0 && (
        <div className="flex flex-col gap-2">
          <label htmlFor="avatar-model" className="text-xs font-bold uppercase tracking-wide text-fg-muted">
            {ta('create.aiModel')}
          </label>
          <select
            id="avatar-model"
            value={effectiveModelId}
            onChange={(e) => setModelId(e.target.value)}
            className={cn(
              'w-full rounded-xl bg-bg-secondary p-3 text-sm text-fg',
              'border border-border focus:border-brand focus:outline-none',
            )}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.deprecated ? ` — ${ta('models.deprecatedOption', { date: m.deprecatedAt ?? '' })}` : ''}
                {m.description ? ` · ${m.description}` : ''}
              </option>
            ))}
          </select>
          {activeModel?.deprecated && (
            <p className="text-[11px] text-warning">
              {activeModel.deprecatedAt
                ? ta('models.deprecatingOn', { date: activeModel.deprecatedAt })
                : ta('models.deprecating')}
            </p>
          )}
        </div>
      )}

      <p className="text-[11px] text-fg-muted">
        <span className="font-semibold">{ta('create.templateLabel')}</span>{' '}
        {activeTemplate ? activeTemplate.name : ta('create.templateNone')}
        {' · '}
        <span className="font-semibold">{ta('create.modelLabel')}</span>{' '}
        {activeModel ? activeModel.name : effectiveModelId}
      </p>

      {error && (
        <div className="rounded-xl bg-danger/15 p-3 text-sm text-danger">{error}</div>
      )}

      {activeJobId && (
        <GeneratingOverlay jobId={activeJobId} onComplete={onComplete} onFailed={onFailed} />
      )}

      {inspectTemplateId && (
        <TemplateInspector
          templateId={inspectTemplateId}
          templates={templates}
          activeModelName={activeModel?.name ?? effectiveModelId}
          onClose={() => setInspectTemplateId(null)}
        />
      )}
    </div>
  );
}

interface TemplateInspectorProps {
  templateId: string;
  templates: AvatarTemplate[];
  activeModelName: string;
  onClose: () => void;
}

/**
 * Phase 5+ — entry point for direct 3D creation paths (skip the 2D step).
 * Tappable card that opens Direct3dModal. Shown at the top of AvatarCreate
 * so it's discoverable but doesn't dominate the existing 2D-first flow.
 */
function Direct3dCard({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation('avatars');
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center justify-between gap-3 rounded-2xl border border-brand bg-brand/10 px-4 py-3 text-left transition-opacity active:opacity-80"
    >
      <div className="flex items-center gap-3">
        <span className="rounded-xl bg-brand/20 p-2 text-brand">
          <Sparkles aria-hidden className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm font-bold text-fg">{t('create.quick3dTitle')}</p>
          <p className="mt-0.5 text-[11px] text-fg-muted">{t('create.quick3dSubtitle')}</p>
        </div>
      </div>
      <span aria-hidden className="text-xl font-bold text-brand">›</span>
    </button>
  );
}

export function TemplateInspector({ templateId, templates, activeModelName, onClose }: TemplateInspectorProps) {
  const { t } = useTranslation('avatars');
  useLivelloIndietro(true, onClose);
  const [copied, setCopied] = useState(false);
  const { data: detail, isFetching, error } = useGetTemplateDetailQuery(templateId);
  const fallback = templates.find((tpl) => tpl._id === templateId);
  const name = detail?.name ?? fallback?.name ?? t('create.inspector.fallbackName');
  const description = detail?.description ?? fallback?.description ?? '';
  const systemPrompt = detail?.systemPrompt ?? '';

  const handleCopy = useCallback(async () => {
    if (!systemPrompt) return;
    try {
      await navigator.clipboard.writeText(systemPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('[TemplateInspector] copy failed', err);
    }
  }, [systemPrompt]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-xl flex-col rounded-t-2xl bg-bg-secondary p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-bold text-fg">{name}</h2>
            {description && <p className="mt-0.5 text-xs text-fg-muted">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="shrink-0 rounded-full border border-border bg-bg p-1.5 text-fg-muted"
          >
            ✕
          </button>
        </div>

        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wide text-fg-muted">{t('create.inspector.systemPrompt')}</p>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!systemPrompt}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              copied
                ? 'border-success bg-success/20 text-success'
                : 'border-border bg-bg text-fg',
              !systemPrompt && 'opacity-50',
            )}
          >
            {copied ? t('create.inspector.copied') : t('create.inspector.copy')}
          </button>
        </div>

        <pre className="mb-3 min-h-[80px] flex-1 overflow-auto rounded-xl border border-border bg-bg p-3 text-[11px] leading-relaxed text-fg whitespace-pre-wrap font-mono">
          {isFetching && !detail
            ? t('create.inspector.loading')
            : error
              ? t('create.inspector.loadFailed')
              : systemPrompt || t('create.inspector.empty')}
        </pre>

        <p className="text-[11px] text-fg-muted">{t('create.inspector.generatedBy', { model: activeModelName })}</p>
      </div>
    </div>
  );
}
